import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { api, pad } from '../../lib/api';
import { TEMPLATE_PARTS } from '../../lib/u99';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { PageHead } from '../../components/u99/PageHead';
import { Label } from '../../components/u99/Label';

export const DoctrineRow = ({ d, i }) => (
  <Reveal delay={Math.min(i, 6) * 0.06}>
    <Link to={`/u99/doctrines/${d.slug}`} className="group grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr_auto] items-start sm:items-baseline gap-x-4 sm:gap-x-8 gap-y-3 py-7 border-b border-white/[0.07] hover:border-amber-300/40 transition-colors duration-500" data-testid={`doctrine-row-${d.slug}`}>
      <span className="font-celestial text-2xl sm:text-3xl text-slate-600 group-hover:text-amber-300 transition-colors duration-500">{pad(d.number)}</span>
      <div>
        <h3 className="font-celestial text-lg sm:text-xl lg:text-2xl leading-snug text-slate-100 group-hover:text-amber-100 transition-colors duration-500">{d.title}</h3>
        {d.subtitle && <p className="text-sm text-slate-500 mt-1.5">{d.subtitle}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Label id={d.label} size="xs" />
          <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-600">{d.reading_minutes} min · {d.has_template ? 'six-part template' : 'full text'}</span>
        </div>
      </div>
      <span className="hidden sm:flex font-mono-precision text-[10px] uppercase tracking-[0.2em] items-center gap-2 text-amber-300/80">Read <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></span>
    </Link>
  </Reveal>
);

export default function U99Doctrines() {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    document.title = 'Doctrines — United 99 Unleashed';
    api.get('/doctrines').then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-5 lg:px-10" data-testid="u99-doctrines-page">
      <PageHead eyebrow="Doctrines · Creative doctrine, guidance and hypothesis" image="/u99/helix-snowflake-blood-cells.jpg" imageAlt="Orange double helix with snowflake blood cells — the internal binary flow of the soul DNA" title={<>Twenty statements, <span className="text-amber-300">one frame</span></>} lede="Every doctrine is presented through the same six parts, so a reader always knows what is being claimed, how it is proposed to work, and how far the evidence goes. Batch 1 is live; the rest are held as source material until approved." />

      <section className="py-8 lg:py-12 border-t border-white/[0.07]" data-testid="template-explainer">
        <Reveal><Eyebrow>The template</Eyebrow></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          {TEMPLATE_PARTS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.05}>
              <div className="rounded-2xl border border-white/[0.07] p-5 h-full">
                <span className="font-celestial text-amber-300/80 text-sm">{p.n}</span>
                <p className="mt-2 font-celestial text-base text-slate-100 leading-snug">{p.title}</p>
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">{p.hint}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-8 lg:py-12 border-t border-white/[0.07]">
        <Reveal><Eyebrow>Published</Eyebrow></Reveal>
        <div className="border-t border-white/[0.07]">
          {docs === null && <p className="py-10 font-mono-precision text-xs text-slate-600 tracking-[0.2em]">LOADING…</p>}
          {docs?.map((d, i) => <DoctrineRow key={d.id} d={d} i={i} />)}
          {docs?.length === 0 && <p className="py-10 font-mono-precision text-xs text-slate-600 tracking-[0.2em]" data-testid="doctrines-empty">NOTHING PUBLISHED YET</p>}
        </div>
        <p className="mt-8 font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-600" data-testid="doctrines-held-note">Batch 1 of 20 published · remaining doctrines held as source material</p>
      </section>
    </main>
  );
}
